import { useState } from "react";
import { v4 } from "uuid";
import { emailField } from "@/pages/quiz/base/questions/contactField/validationRules";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { useQuizContext } from "@/pages/quiz/context";
import { useParams } from "react-router-dom";
import { useManageInputFields } from "@/pages/quiz/hooks/useManageInputFields";
import { useGetIsYounger64 } from "@/pages/quiz/base/hooks/useGetIs64OrOver";
import { usePrefillQuestionValues } from "@/pages/quiz/hooks/usePrefillQuestionValues";
import { getDisclaimersData } from "@/pages/quiz/base/questions/disclaimerAcknowledgment/common";
import { submitQuizAnswer } from "@/services/quizServices";
import { EMAIL_TAKEN_ERROR_MESSAGE } from "@/constants";
import { Input } from "@/pages/quiz/15000/components/Input";
import { Button } from "@/pages/quiz/15000/components/Button";
import { Checkbox } from "@/pages/quiz/15000/questions/ContactEmail/components/checkbox";
import Modal from "@/components/common/generalModal";
import Description from "@/pages/quiz/base/questions/disclaimerAcknowledgment/components/Description";
import { Disclaimer } from "@/pages/quiz/15000/questions/ContactEmail/components/disclaimer";
import { useGetGender } from "@/pages/quiz/base/hooks/useGetGender";

type ConsentItem = {
  id: string;
  label: string;
  isShown?: boolean;
};

type FormFieldName = "email";
type EmailFieldsValue = Record<FormFieldName, string> & { agree: boolean };

const validationFields = {
  email: emailField,
};

const ContactEmailField = ({
  questionValue,
  question,
  uploadUserId,
  setQuestionValue,
  setQuestionAnswer,
}: QuizQuestionProps<EmailFieldsValue>) => {
  const { initialQuestions, answers } = useQuizContext();
  const isYounger64 = useGetIsYounger64();
  const { isMale } = useGetGender();
  const [isConsentsModalOpened, setIsConsentsModalOpened] = useState(false);

  const { quizId } = useParams();
  const { registerField, error, setFieldError, validateField, onValidateForm } =
    useManageInputFields(
      {
        setQuestionValue: (values) =>
          setQuestionValue({ ...questionValue, ...values }),
        questionValue: { email: questionValue.email },
      },
      validationFields
    );

  const consents: ConsentItem[] = [
    {
      id: 'side_effects_ack',
      label: 'Acknowledge the potential side effects',
      isShown: true,
    },
    {
      id: 'side_effects_pregnancy',
      label: 'Potential Side Effects & Pregnancy Precautions',
      isShown: !isMale,
    },
    {
      id: 'telehealth',
      label: 'Telehealth consent',
      isShown: true,
    },
    {
      id: 'glp1_microdosing',
      label: 'GLP-1 Medication Information (low-dose microdosing)',
      isShown: true,
    },
    {
      id: 'medication_adjustment',
      label: 'Medication adjustment acknowledgement and consent',
      isShown: true,
    },
    {
      id: 'medicare_non_participation',
      label: 'Medicare & Medicaid non-participation notice',
      isShown: !isYounger64,
    },
  ].filter(c => c.isShown);

  usePrefillQuestionValues({
    fields: ["email"],
    setValue: (values) => {
      setQuestionValue({ ...questionValue, ...values });

      Object.entries(values).forEach(([key, value]) => {
        setFieldError(key, validateField(key as FormFieldName, value ?? ""));
      });
    },
  });

  const isFormAnswered =
    !!questionValue.email && !error.email && !!questionValue.agree;
  const email = questionValue.email?.toLowerCase();

  const handleContactsFieldsTrigger = async () => {
    const contactFieldsQuestion = initialQuestions?.find(
      (question) => question.name === "contacts-fields-cpa"
    );
    const fullNameQuestion = Object.values(answers).find(
      (answer) =>
        answer.blockName === "custom-full-name-dob" ||
        answer.blockName === "contact-full-name"
    );
    const addressQuestion = Object.values(answers).find(
      (answer) =>
        answer.blockName === "custom-contact-for-available-plans" ||
        answer.blockName === "custom-contact-for-available-plans-address"
    );

    if (contactFieldsQuestion) {
      const body = {
        phone: (addressQuestion?.value as any)?.phone,
        firstName:
          (fullNameQuestion?.value as any)?.firstName ||
          (addressQuestion?.value as any)?.firstName,
        secondName:
          (fullNameQuestion?.value as any)?.lastName ||
          (addressQuestion?.value as any)?.lastName,
        email,
      };

      await submitQuizAnswer({
        id: contactFieldsQuestion.id,
        userId: uploadUserId ?? "",
        quizId,
        value: body,
      });
    }
  };

  const handleAgree = () =>
    setQuestionValue({
      ...questionValue,
      agree: !questionValue?.agree,
    });

  const onSubmit = async () => {
    if (error.email || !isFormAnswered) {
      return;
    }

    try {
      window.dataLayer.push({
        eventId: v4(),
        event: "email_submitted",
      });
    } catch (error) {
      console.error(error);
    }

    if (uploadUserId && quizId) {
      const serverErrors = await submitQuizAnswer({
        id: question.id,
        userId: uploadUserId,
        quizId,
        value: questionValue,
      });

      if (serverErrors?.status === 409) {
        setFieldError("email", EMAIL_TAKEN_ERROR_MESSAGE);
      } else {
        await handleContactsFieldsTrigger();
        setQuestionAnswer(
          {
            ...questionValue,
            email,
          },
          true,
          true
        );
      }
    } else {
      setQuestionAnswer(questionValue);
    }
  };

  return (
    <>
      <Input
        {...registerField("email")}
        label="Email address"
        placeholder="example@email.com"
        errorMessage={error?.email}
      />
      <div className="mt-6 text-sm font-normal">
        <p
          role="button"
          className="underline cursor-pointer"
          onClick={() => setIsConsentsModalOpened(true)}
        >
          Consent & Acknowledgement
        </p>
        <p className="text-sm text-secondary mt-2">
          By clicking Continue, I confirm that I have answered this survey truthfully to help tailor my
          treatment. I agree to the following:
        </p>
        <ul className="list-disc pl-5 mt-4 space-y-2 text-sm text-black">
          {consents.map((c) => (
            <li key={c.id}>{c.label}</li>
          ))}
        </ul>
      </div>
      <div className="mt-2">
        <Checkbox
          onChange={handleAgree}
          isChecked={questionValue?.agree || false}
          name="agree"
          label="I have read and agree to the consents and acknowledgments above"
        />
      </div>
      <Button disabled={!isFormAnswered} onClick={onValidateForm(onSubmit)} />
      <Modal
        backgroundStyle="blur"
        isScrollable
        isOpen={isConsentsModalOpened}
        onClose={() => setIsConsentsModalOpened(false)}
      >
        <div className="flex flex-col justify-center gap-4 sm:gap-8 max-w-[768px] min-h-[calc(100vh-80px)] mx-auto rounded-3xl">
          <div className="z-10 flex justify-end py-2 bg-white">
            <button
              onClick={() => setIsConsentsModalOpened(false)}
              aria-label="Close"
              className="text-sm text-black"
            >
              Close x
            </button>
          </div>
          <p className="section-title">
            Please review and agree to the following precautions
          </p>
          <div className="space-y-4">
            <Description
              description={
                "Read the following for more information about this product and its potential side effects."
              }
            />
            {getDisclaimersData(isYounger64, isMale).map((disclaimerData, idx) => (
              <Disclaimer key={idx} disclaimerData={disclaimerData} />
            ))}
          </div>
          <button
            onClick={() => setIsConsentsModalOpened(false)}
            className="text-sm text-black"
            aria-label="Close"
          >
            Close x
          </button>
        </div>
      </Modal>
    </>
  );
};

export { ContactEmailField };
